import {
    DisclosureAcknowledgementResponseLabels,
    formattedDateTimeDefault,
    UserId,
} from "@sp-crm/core";
import { GuestEmailDisplay } from "components/contact-display/guest-email-display";
import { GuestPhoneDisplay } from "components/contact-display/guest-phone-display";
import { Badge, BadgeProps } from "components/ui/badge";
import { DisclosureAcknowledgementFragmentFragment } from "generated/graphql";
import { DisclosureAcknowledgementResponse } from "generated/graphql-types";
import React from "react";

export type AcknowledgementSource =
    | { kind: "email" }
    | { kind: "manual"; addedByName: string };

export const resolveAcknowledgementSource = (
    acknowledgement: DisclosureAcknowledgementFragmentFragment,
    users: Record<UserId, { name: string }>,
): AcknowledgementSource => {
    if (!acknowledgement.createdBy) {
        return { kind: "email" };
    }
    return {
        kind: "manual",
        addedByName: users[acknowledgement.createdBy]?.name ?? "Agent",
    };
};

export const sortAcknowledgementsNewestFirst = (
    acknowledgements: DisclosureAcknowledgementFragmentFragment[],
): DisclosureAcknowledgementFragmentFragment[] =>
    [...acknowledgements].sort((a, b) => b.dateAdded.localeCompare(a.dateAdded));

const responseBadgeTypes: Record<DisclosureAcknowledgementResponse, BadgeProps["type"]> =
    {
        [DisclosureAcknowledgementResponse.Agreed]: "success",
        [DisclosureAcknowledgementResponse.Declined]: "error",
        [DisclosureAcknowledgementResponse.NotRequired]: "info",
    };

const sourceDescription = (source: AcknowledgementSource): string => {
    switch (source.kind) {
        case "email":
            return "Via email";
        case "manual":
            return `Added manually by ${source.addedByName}`;
        default: {
            const exhaustiveCheck: never = source;
            throw new Error(
                `Unhandled acknowledgement source: ${JSON.stringify(exhaustiveCheck)}`,
            );
        }
    }
};

interface ResponseBadgeProps {
    response: DisclosureAcknowledgementResponse | null;
}

const ResponseBadge: React.FC<ResponseBadgeProps> = props => {
    const { response } = props;
    if (!response) {
        return null;
    }
    return (
        <div className="ml-auto shrink-0 whitespace-nowrap">
            <Badge
                value={DisclosureAcknowledgementResponseLabels[response]}
                type={responseBadgeTypes[response]}
            />
        </div>
    );
};

interface DisclosureAcknowledgementCardProps {
    acknowledgement: DisclosureAcknowledgementFragmentFragment;
    source: AcknowledgementSource;
}

export const DisclosureAcknowledgementCard: React.FC<
    DisclosureAcknowledgementCardProps
> = props => {
    const { acknowledgement, source } = props;
    const details = [
        formattedDateTimeDefault(acknowledgement.dateAdded),
        sourceDescription(source),
        acknowledgement.ipAddress ? `IP ${acknowledgement.ipAddress}` : "",
    ].filter(Boolean);
    const hasHeader = !!acknowledgement.name || !!acknowledgement.response;
    const hasContact = !!acknowledgement.email || !!acknowledgement.phone;

    return (
        <li className="rounded-lg border border-gray-200 p-3 space-y-1 text-sm">
            {hasHeader ? (
                <div
                    data-testid="acknowledgement-header"
                    className="flex items-start justify-between gap-2">
                    {acknowledgement.name ? (
                        <p
                            data-testid="acknowledgement-name"
                            className="min-w-0 break-words font-medium text-gray-900">
                            {acknowledgement.name}
                        </p>
                    ) : null}
                    <ResponseBadge response={acknowledgement.response} />
                </div>
            ) : null}
            {hasContact ? (
                <div
                    data-testid="acknowledgement-contact"
                    className="flex flex-wrap gap-x-4 gap-y-1">
                    <GuestEmailDisplay value={acknowledgement.email} />
                    <GuestPhoneDisplay value={acknowledgement.phone} label={null} />
                </div>
            ) : null}
            <p className="text-xs text-gray-500 break-words">{details.join(" · ")}</p>
        </li>
    );
};
